// Unseen forecast arrivals, held in a small module-level store.
//
// Only the SSE push path (commands/reconcile) marks a fire here, so a fire
// that streams in while the user is watching gets a pulse + a persistent "new"
// ring on its timeline mark until acknowledged (hover/click/scrub). The pure
// transforms live in lib/forecast/arrivals.js.
import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";

import { markArrived, acknowledge } from "../lib/forecast/arrivals.js";

interface ArrivalsState {
  /** Moments (ISO fire_ts) of arrivals not yet acknowledged. */
  unseen: Set<string>;
}

/** Starts empty; a page load never pre-populates it. */
export const arrivalsStore = createStore<ArrivalsState>(() => ({ unseen: new Set<string>() }));

export const useArrivals = <T>(selector: (s: ArrivalsState) => T): T =>
  useStore(arrivalsStore, selector);

export const getArrivals = (): ArrivalsState => arrivalsStore.getState();

/**
 * The `moment` is the ISO fire_ts of the fire. Only setState (→ notify) when the set
 * actually changes, so a re-delivered/idempotent event doesn't churn subscribers.
 */
export function markArrival(moment: string): void {
  const { unseen } = arrivalsStore.getState();
  const next = markArrived(unseen, moment);
  if (next !== unseen) arrivalsStore.setState({ unseen: next });
}

export function acknowledgeArrival(moment: string): void {
  const { unseen } = arrivalsStore.getState();
  const next = acknowledge(unseen, moment);
  if (next !== unseen) arrivalsStore.setState({ unseen: next });
}

/**
 * The store is a module-level singleton, so its state would otherwise leak across
 * mounts (StrictMode remount, Fast Refresh, tests). Callers invoke this on unmount.
 */
export function resetArrivalsStore(): void {
  arrivalsStore.setState({ unseen: new Set<string>() }, true);
}
